import * as THREE from 'three';
import { SparkRenderer, SplatMesh, SparkControls, EnvironmentMapLoader } from '@ydrive/sparky';
import { clampWorldXZToCircle, dollyCameraAlongView } from './measureUtils';

// ─────────────────────────────────────────────────────────────────────────────
// SparkViewer — a thin, gs-viewer-compatible adapter around the Spark renderer.
//
// gs-viewer's `Viewer` is an all-in-one class (scene + camera + renderer + loop
// + controls + loading). Spark only ships primitives (SparkRenderer, SplatMesh,
// SparkControls), so this class re-implements the slice of the `Viewer` API that
// SceneViewer's single-scene path uses, for .ply / .rad / .gaus / .gm files
// which Spark loads natively. gs-viewer remains in use only for segmented
// (manifest) scenes.
//
// Spark's .gaus support is version 4 / single-segment only, and .gm must not
// use LOSSY compression — older assets fail to load and need re-export from
// the Yugo pipeline (not a code fix).
//
// Implemented on top of Spark ≥2.3.0:
//   - click-to-move: SparkControls' built-in double-click/tap fly-to-surface
//     (raycastTargets + flyTo). `maxClickToMoveDistance = 0` disables it, for
//     parity with the gs-viewer freeze convention tools rely on.
//   - raycaster shim: setFromCameraAndScreenPosition + intersectSplatMesh,
//     backed by SplatMesh.measurePoint() (precise alpha-weighted surface
//     query) — hits expose `.origin` like gs-viewer's raycaster, which is
//     what MeasureTool / FlythroughTool / viewerSpatialUtils read.
//
// Intentionally NOT implemented — future work (remaining tools stay disabled
// on the Spark path):
//   - geodetic viewer bridge (observerCoordinates / getCameraGeodeticPosition)
//   - segmented-manifest loading
// Callers in SceneViewer guard these behind `usesSparkRenderer(scene)`.
// ─────────────────────────────────────────────────────────────────────────────

export interface SparkViewerOptions {
  rootElement: HTMLElement;
  cameraFoV?: number;
  cameraFar?: number;
  initialCameraPosition?: [number, number, number];
  initialCameraLookAt?: [number, number, number];
  sceneType?: string;
}

export interface SparkMovementBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

export interface SparkMovementCircle {
  centerX: number;
  centerZ: number;
  radius: number;
}

interface AddSplatSceneOptions {
  onProgress?: (progress: number | undefined, progressLabel?: string) => void;
  /** Equirectangular EXR loaded as the scene background (gs-viewer option name). */
  environmentMapPath?: string;
  /** Raw data coordinate convention. 'zup' applies Euler(-π/2,0,0) instead of the
   *  standard GS Y-down flip so the scene renders upright in Three.js Y-up world space. */
  coordinateFrame?: 'zup' | 'yup';
  // remaining gs-viewer options (showLoadingUI, image poses…) are ignored
  [key: string]: unknown;
}

/**
 * Move `camera` to the median center of a SplatMesh's splats.
 *
 * Uses getQuantileBounds() with a retry loop — paged .rad files have no
 * CPU-side splats immediately after initialized resolves, so we poll every
 * 200ms until pages stream in (up to maxWaitMs). Returns true if the camera
 * was moved, false if it timed out or failed.
 */
export async function centerCameraOnSplats(
  camera: THREE.PerspectiveCamera,
  mesh: SplatMesh,
  { percentile = 0.9, maxWaitMs = 6000 }: { percentile?: number; maxWaitMs?: number } = {}
): Promise<boolean> {
  try {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const { center, box, numSamples } = (mesh as any).getQuantileBounds({ percentile });
      if (numSamples > 0) {
        mesh.updateMatrixWorld(true);
        camera.position.copy(center).applyMatrix4(mesh.matrixWorld);
        const size = box.getSize(new THREE.Vector3()).applyQuaternion(mesh.quaternion);
        camera.far = Math.max(camera.far, size.length() * 4);
        camera.updateProjectionMatrix();
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    console.warn('[SparkViewer] centerCameraOnSplats timed out waiting for splat data');
    return false;
  } catch (error) {
    console.warn('[SparkViewer] centerCameraOnSplats unavailable:', error instanceof Error ? error.message : error);
    return false;
  }
}

/** Detects whether a scene's gaus URL is a format Spark renders (.ply / .rad / .gaus / .gm). */
export function usesSparkRenderer(gausUrl: string | undefined | null): boolean {
  if (!gausUrl) return false;
  const path = gausUrl.split('?')[0].split('#')[0].toLowerCase();
  return (
    path.endsWith('.ply') ||
    path.endsWith('.rad') ||
    path.endsWith('.gaus') ||
    path.endsWith('.gm')
  );
}

/**
 * gs-viewer-compatible raycaster: pixel-space screen position in, world-space
 * hits with `.origin` out. Backed by SplatMesh.measurePoint(), Spark's precise
 * alpha-weighted surface query (transparent floaters are down-weighted).
 */
export class SparkRaycaster {
  private raycaster = new THREE.Raycaster();

  setFromCameraAndScreenPosition(
    camera: THREE.Camera,
    screenPosition: THREE.Vector2,
    renderDimensions: THREE.Vector2
  ): void {
    const ndc = new THREE.Vector2(
      (screenPosition.x / renderDimensions.x) * 2 - 1,
      -(screenPosition.y / renderDimensions.y) * 2 + 1
    );
    this.raycaster.setFromCamera(ndc, camera);
  }

  intersectSplatMesh(
    splatMesh: SplatMesh,
    outHits: Array<{ origin: THREE.Vector3; distance: number }> = []
  ): Array<{ origin: THREE.Vector3; distance: number }> {
    outHits.length = 0;
    const hit = splatMesh?.measurePoint?.(this.raycaster.ray);
    if (hit) {
      outHits.push({ origin: hit.point.clone(), distance: hit.distance });
    }
    return outHits;
  }
}

export class SparkViewer {
  // ── Public, gs-viewer-compatible surface ─────────────────────────────────
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  /** Alias for `scene` — tools that overlay Three.js objects (SelectionTool bounding box) use this name. */
  get threeScene(): THREE.Scene { return this.scene; }
  splatMesh: SplatMesh | null = null;
  raycaster = new SparkRaycaster();
  /** SceneViewer sets this to 2.0 when it reads 0 — kept for parity. */
  medianCameraDistance = 0;
  /** Camera-freeze shims read/written by tool effects. */
  nextCamera: THREE.PerspectiveCamera;
  previousCamera: THREE.PerspectiveCamera;
  clickTransitioning = false;
  transitioningCameraTarget = false;
  controls: {
    movementSpeed: number;
    lookSpeed: number;
    updateLookDirection: () => void;
  };

  /**
   * gs-viewer freeze convention: tools set this to 0 to lock click-to-move
   * while active, then restore the previous value. Any value other than 0
   * leaves Spark's fly-to enabled (the distance itself is not enforced).
   */
  get maxClickToMoveDistance(): number | null {
    return this._maxClickToMoveDistance;
  }
  set maxClickToMoveDistance(value: number | null) {
    this._maxClickToMoveDistance = value;
    this.syncClickToMoveTargets();
  }

  // ── Internals ─────────────────────────────────────────────────────────────
  private _maxClickToMoveDistance: number | null = null;
  private rootElement: HTMLElement;
  private spark: SparkRenderer;
  private sparkControls: SparkControls;
  private running = false;
  private loading = false;
  private disposed = false;
  private resizeObserver: ResizeObserver | null = null;
  private clock = new THREE.Clock();

  private backgroundTexture: THREE.DataTexture | null = null;
  private hasExplicitCameraPosition = false;
  private needsAutoCenter = false;
  private initialCameraPosition = new THREE.Vector3();
  private initialCameraQuaternion = new THREE.Quaternion();
  private movementBounds: SparkMovementBounds | null = null;
  private movementCircle: SparkMovementCircle | null = null;
  private scriptedCameraActive = false;
  /**
   * Gives an animation tool exclusive ownership of the camera while keeping
   * the renderer running. Navigation updates and movement-bound clamping must
   * not rewrite a scripted orbit pose between animation frames.
   */
  setScriptedCameraActive(active: boolean): void {
    this.scriptedCameraActive = active;
    this.sparkControls.flyTo.cancel();
    const pointerControls = this.sparkControls.pointerControls as any;
    pointerControls.rotateVelocity?.set?.(0, 0, 0);
    pointerControls.moveVelocity?.set?.(0, 0, 0);
  }

  private syncClickToMoveTargets(): void {
    const frozen = this._maxClickToMoveDistance === 0;
    this.sparkControls.raycastTargets =
      frozen || !this.splatMesh ? [] : [this.splatMesh];
  }

  /**
   * Loads an equirectangular EXR as the scene background, fire-and-forget so
   * it never blocks or fails the splat load. The rotation matches the sparkY
   * example default; tweak the yaw if a scene's sky looks misaligned relative
   * to our splat orientation.
   */
  private loadEnvironmentMap(url: string): void {
    const loader = new EnvironmentMapLoader();
    loader
      .loadFromUrl(url, {
        scene: this.scene,
        backgroundRotation: new THREE.Euler(0, -Math.PI / 2, -Math.PI),
      })
      .then((texture) => {
        if (this.disposed) {
          texture.dispose();
          return;
        }
        this.backgroundTexture = texture;
      })
      .catch((err) => {
        console.warn('[SparkViewer] Failed to load environment map:', err);
      });
  }

  constructor(options: SparkViewerOptions) {
    this.rootElement = options.rootElement;

    const { clientWidth, clientHeight } = this.rootElement;
    const width = clientWidth || window.innerWidth;
    const height = clientHeight || window.innerHeight;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      options.cameraFoV ?? 60,
      width / height,
      0.01,
      options.cameraFar ?? 1000
    );
    if (options.initialCameraPosition) {
      this.hasExplicitCameraPosition = true;
      this.camera.position.set(...options.initialCameraPosition);
    } else {
      this.camera.position.set(0, 0, 2);
    }
    this.camera.lookAt(
      options.initialCameraLookAt
        ? new THREE.Vector3(...options.initialCameraLookAt)
        : new THREE.Vector3(0, 0, 0)
    );
    this.initialCameraPosition.copy(this.camera.position);
    this.initialCameraQuaternion.copy(this.camera.quaternion);

    const canvas = document.createElement('canvas');
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    this.rootElement.appendChild(canvas);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height, false);

    this.spark = new SparkRenderer({
      renderer: this.renderer,
      // Paged (.rad) meshes take their full-precision encoding from here;
      // other formats opt into extSplats per-mesh in addSplatScene.
      pagedExtSplats: true,
      // Default minAlpha (0.5/255 ≈ 0.002) renders .rad aerials washed-out;
      // 10/255 ≈ 0.039 cuts ghost splats while keeping fine structural detail.
      minAlpha: 10 / 255,
      // Render budget: default desktop target is ~2.5M splats. Aerial Vexcel
      // reconstructions benefit from 10M+ at altitude where the scene spans
      // millions of splats across the frustum.
      lodSplatCount: 10_000_000,
    });
    this.scene.add(this.spark);

    // Double-click / double-tap flies the camera toward the surface point
    // under the cursor, stopping short of it. Targets are assigned once the
    // splat loads (syncClickToMoveTargets).
    this.sparkControls = new SparkControls({
      canvas,
      preset: options.sceneType === 'aerial' ? 'aerial' : 'walk',
      clickToMove: true,
      flyTo: { standoffDistance: 3.0 },
    });
    this.sparkControls.fpsMovement.moveSpeed = 5;

    this.nextCamera = this.camera.clone();
    this.previousCamera = this.camera.clone();

    // pressMoveSpeed defaults to 0 in Sparky's PointerControls; set it equal to
    // fpsMovement.moveSpeed so click-and-hold moves the camera forward at the same
    // speed as keyboard movement. Kept in sync via the movementSpeed setter below.
    this.sparkControls.pointerControls.pressMoveSpeed = this.sparkControls.fpsMovement.moveSpeed;

    const self = this;
    this.controls = {
      get movementSpeed() { return self.sparkControls.fpsMovement.moveSpeed; },
      set movementSpeed(v: number) {
        self.sparkControls.fpsMovement.moveSpeed = v;
        self.sparkControls.pointerControls.pressMoveSpeed = v;
      },
      get lookSpeed() { return self.sparkControls.pointerControls.rotateSpeed; },
      set lookSpeed(v: number) { self.sparkControls.pointerControls.rotateSpeed = v; },
      updateLookDirection() { /* no-op: Spark controls drive the camera directly */ },
    };

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.rootElement);
  }

  private resize() {
    if (this.disposed) return;
    const width = this.rootElement.clientWidth || window.innerWidth;
    const height = this.rootElement.clientHeight || window.innerHeight;
    const canvas = this.renderer.domElement;
    if (canvas.width !== width || canvas.height !== height) {
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
  }

  /**
   * Loads a single .ply / .rad / .gaus / .gm file. Resolves once the splat is
   * ready. Spark detects the format from the URL extension (query/hash are
   * stripped); .gaus has no magic-byte fallback, so the URL path must keep its
   * extension.
   */
  async addSplatScene(url: string, options: AddSplatSceneOptions = {}): Promise<void> {
    if (this.disposed) return;
    this.loading = true;

    const onProgress = options.onProgress;
    const onMeshProgress = onProgress
      ? (event: ProgressEvent) => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        }
      : undefined;

    // .rad streams lazily via the pager (full precision comes from the
    // renderer's pagedExtSplats). Everything else fully decodes with extSplats
    // for full-precision centers — the default packed encoding quantizes
    // centers to float16, which shows up as striping far from the origin.
    const paged = /\.rad$/i.test(url.split('?')[0].split('#')[0]);
    const mesh = new SplatMesh(
      paged
        ? { url, paged: true, onProgress: onMeshProgress }
        : { url, extSplats: true, onProgress: onMeshProgress }
    );

    // Splat data uses a Y-down convention; apply the same coordinate flip
    // gs-viewer applies internally (Viewer.js quaternion (0.5, 0.5, 0.5, -0.5))
    // so all formats render upright. coordinateFrame is MultiSceneViewer metadata only.
    mesh.quaternion.set(0.5, 0.5, 0.5, -0.5);

    try {
      await mesh.initialized;
    } finally {
      this.loading = false;
    }

    if (this.disposed) {
      mesh.dispose?.();
      return;
    }

    this.splatMesh = mesh;
    this.scene.add(mesh);
    this.syncClickToMoveTargets();

    if (!this.hasExplicitCameraPosition) {
      this.needsAutoCenter = true;
    }

    if (options.environmentMapPath) {
      this.loadEnvironmentMap(options.environmentMapPath);
    }
  }

  /** Updates the LOD render budget (splat count target) at runtime. */
  setLodSplatCount(count: number): void {
    this.spark.lodSplatCount = count;
  }

  /** Returns the current LOD render budget. */
  getLodSplatCount(): number {
    return this.spark.lodSplatCount ?? 10_000_000;
  }

  /** Restrict every Spark control path (keys, pointer movement and fly-to) to one world-space box. */
  setMovementBounds(bounds: SparkMovementBounds | null): void {
    this.movementBounds = bounds;
    this.enforceMovementBounds();
  }

  /** Restrict camera navigation to a circular XZ area. */
  setMovementCircle(circle: SparkMovementCircle | null): void {
    this.movementCircle = circle;
    this.enforceMovementBounds();
  }

  /**
   * Frame an XZ footprint from above so the viewport is covered by scene data.
   * `cover` deliberately crops a little at the long edges to avoid exposing the
   * black exterior when the browser aspect ratio differs from the scene.
   */
  fitCameraToXZBounds(bounds: SparkMovementBounds, lookAtY: number, cover = 0.88): void {
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerZ = (bounds.minZ + bounds.maxZ) / 2;
    const width = bounds.maxX - bounds.minX;
    const depth = bounds.maxZ - bounds.minZ;
    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * this.camera.aspect);
    const heightForDepth = depth / (2 * Math.tan(verticalFov / 2));
    const heightForWidth = width / (2 * Math.tan(horizontalFov / 2));
    const cameraY = lookAtY + Math.min(heightForDepth, heightForWidth) * cover;

    this.sparkControls.flyTo.cancel();
    this.camera.up.set(0, 0, -1);
    this.camera.position.set(centerX, cameraY, centerZ);
    this.camera.lookAt(centerX, lookAtY, centerZ);
    this.camera.updateMatrixWorld(true);
    this.initialCameraPosition.copy(this.camera.position);
    this.initialCameraQuaternion.copy(this.camera.quaternion);
    this.nextCamera.copy(this.camera);
    this.previousCamera.copy(this.camera);
    this.enforceMovementBounds();
  }

  private enforceMovementBounds(): void {
    const bounds = this.movementBounds;
    const circle = this.movementCircle;
    if (!bounds && !circle) return;
    const position = this.camera.position;
    const boxX = bounds ? THREE.MathUtils.clamp(position.x, bounds.minX, bounds.maxX) : position.x;
    const y = bounds ? THREE.MathUtils.clamp(position.y, bounds.minY, bounds.maxY) : position.y;
    const boxZ = bounds ? THREE.MathUtils.clamp(position.z, bounds.minZ, bounds.maxZ) : position.z;
    const circular = circle
      ? clampWorldXZToCircle(
          { x: boxX, y, z: boxZ },
          { x: circle.centerX, z: circle.centerZ },
          circle.radius,
        )
      : { x: boxX, y, z: boxZ };
    const { x, z } = circular;
    if (x === position.x && y === position.y && z === position.z) return;

    position.set(x, y, z);
    this.camera.updateMatrixWorld(true);
    this.sparkControls.flyTo.cancel();
    const pointerControls = this.sparkControls.pointerControls as any;
    pointerControls.moveVelocity?.set?.(0, 0, 0);
    this.nextCamera.copy(this.camera);
    this.previousCamera.copy(this.camera);
  }

  /** Give the real-estate navigation handler exclusive ownership of manual input. */
  enableAerialNavigation(): () => void {
    const fpsEnabled = this.sparkControls.fpsMovement.enable;
    const pointerEnabled = this.sparkControls.pointerControls.enable;
    const clickToMove = this.sparkControls.clickToMove;
    const pressMoveSpeed = this.sparkControls.pointerControls.pressMoveSpeed;
    this.sparkControls.fpsMovement.enable = false;
    this.sparkControls.pointerControls.enable = false;
    this.sparkControls.pointerControls.pressMoveSpeed = 0;
    this.sparkControls.clickToMove = false;
    this.sparkControls.flyTo.cancel();
    return () => {
      this.sparkControls.fpsMovement.enable = fpsEnabled;
      this.sparkControls.pointerControls.enable = pointerEnabled;
      this.sparkControls.pointerControls.pressMoveSpeed = pressMoveSpeed;
      this.sparkControls.clickToMove = clickToMove;
    };
  }

  syncManualNavigation(): void {
    this.sparkControls.flyTo.cancel();
    this.enforceMovementBounds();
    this.camera.updateMatrixWorld(true);
    this.nextCamera.copy(this.camera);
    this.previousCamera.copy(this.camera);
  }

  /** Move toward/away from the current look-at direction, including its vertical component. */
  dolly(distance: number): void {
    this.sparkControls.flyTo.cancel();
    dollyCameraAlongView(this.camera, distance);
    this.enforceMovementBounds();
    const pointer = this.sparkControls.pointerControls as any;
    pointer.moveVelocity?.set?.(0, 0, 0);
    this.nextCamera.copy(this.camera);
    this.previousCamera.copy(this.camera);
  }

  /** Teleports the camera back to the position and orientation it had at load time. */
  resetCamera(): void {
    this.sparkControls.flyTo.cancel();
    this.camera.position.copy(this.initialCameraPosition);
    this.camera.quaternion.copy(this.initialCameraQuaternion);
    this.camera.updateMatrixWorld();
    const pc = this.sparkControls.pointerControls as any;
    pc.rotateVelocity?.set(0, 0, 0);
    pc.moveVelocity?.set(0, 0, 0);
    this.nextCamera.copy(this.camera);
    this.previousCamera.copy(this.camera);
  }

  /** Starts the render loop. */
  start() {
    if (this.running || this.disposed) return;
    this.running = true;
    this.clock.start();
    // Pass the loop timestamp through so control movement tracks the vsync
    // cadence (sampling performance.now() inside update jitters).
    this.renderer.setAnimationLoop((time: number) => {
      if (this.disposed) return;
      if (!this.scriptedCameraActive) {
        this.sparkControls.update(this.camera, this.camera, time);
        this.enforceMovementBounds();
      }
      this.renderer.render(this.scene, this.camera);
    });
    // Auto-center runs after the loop starts so the pager can stream chunks
    // during the polling intervals. Fire-and-forget; errors are swallowed inside.
    if (this.needsAutoCenter && this.splatMesh) {
      this.needsAutoCenter = false;
      centerCameraOnSplats(this.camera, this.splatMesh).catch(() => {});
    }
  }

  /** Stops the render loop without tearing down GPU resources. */
  stop() {
    this.running = false;
    this.renderer.setAnimationLoop(null);
  }

  /** Renders synchronously and returns a JPEG data URL capped at `maxWidth` px. */
  captureFrame(maxWidth = 1280): string {
    this.renderer.render(this.scene, this.camera);
    const source = this.renderer.domElement;
    const scale = source.width > maxWidth ? maxWidth / source.width : 1;
    const offscreen = document.createElement('canvas');
    offscreen.width = Math.round(source.width * scale);
    offscreen.height = Math.round(source.height * scale);
    const ctx = offscreen.getContext('2d');
    if (!ctx) return '';
    ctx.drawImage(source, 0, 0, offscreen.width, offscreen.height);
    return offscreen.toDataURL('image/jpeg', 0.85);
  }

  /** Moves the camera. Only world-space `position` is supported (no geodetic). */
  async teleportTo(target: { position?: number[]; lookAt?: number[]; up?: number[]; geoPosition?: number[]; callback?: () => Promise<void> }): Promise<void> {
    await target.callback?.();
    if (target.position && target.position.length >= 3) {
      this.sparkControls.flyTo.cancel();
      const previousUp = this.camera.up.clone();
      this.camera.position.set(target.position[0], target.position[1], target.position[2]);
      if (target.up && target.up.length >= 3) this.camera.up.set(target.up[0], target.up[1], target.up[2]);
      if (target.lookAt && target.lookAt.length >= 3) {
        this.camera.lookAt(target.lookAt[0], target.lookAt[1], target.lookAt[2]);
      }
      // Preserve the saved-view orientation without changing the navigation up axis.
      if (target.up) this.camera.up.copy(previousUp);
      this.camera.updateMatrixWorld();
      // Sync Spark's motion buffers so controls don't snap back on next frame
      const pc = this.sparkControls.pointerControls as any;
      pc.rotateVelocity?.set(0, 0, 0);
      pc.moveVelocity?.set(0, 0, 0);
      (this as any).nextCamera?.copy(this.camera);
      (this as any).previousCamera?.copy(this.camera);
    }
  }

  /** No geodetic context for a single .ply/.rad scene. */
  getCameraGeodeticPosition(): null {
    return null;
  }

  isLoadingOrUnloading(): boolean {
    return this.loading;
  }

  getRenderDimensions(out: THREE.Vector2): THREE.Vector2 {
    return out.set(this.renderer.domElement.width, this.renderer.domElement.height);
  }

  /** Centers the camera on the loaded splat using the median of splat centers (not bounding-box center). */
  async centerOnSplats(options?: { percentile?: number }): Promise<boolean> {
    if (!this.splatMesh) return false;
    return centerCameraOnSplats(this.camera, this.splatMesh, options);
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.stop();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.splatMesh) {
      this.scene.remove(this.splatMesh);
      this.splatMesh.dispose?.();
      this.splatMesh = null;
    }
    if (this.backgroundTexture) {
      this.scene.background = null;
      this.backgroundTexture.dispose();
      this.backgroundTexture = null;
    }
    this.scene.remove(this.spark);
    this.spark.dispose?.();
    this.renderer.dispose();
    // Release the context immediately when switching scenes or remounting in dev.
    this.renderer.forceContextLoss();
    this.renderer.domElement.remove();
  }
}
